'use client';

import { entityUrl } from '@/lib/entity-url';
import React, { useMemo } from 'react';
import Link from 'next/link';
import { useFirestoreCrypto } from '../../hooks/useFirestoreCrypto';

interface TokenSymbolProps {
  symbol: string; // Without $ prefix
}

/**
 * TokenSymbol component - renders $SYMBOL as a link to the token's tag page
 * Looks up the token in crypto data to find the associated tag
 * Falls back to plain text if token not found
 */
export const TokenSymbol: React.FC<TokenSymbolProps> = ({ symbol }) => {
  const { data: cryptoData } = useFirestoreCrypto();

  // Find the token and its associated tag
  const tokenInfo = useMemo(() => {
    if (!cryptoData) return null;

    const upperSymbol = symbol.toUpperCase();

    // Check Abstract Tokens first (most common)
    const abstractToken = cryptoData.abstract_tokens?.find(
      t => t.symbol.toUpperCase() === upperSymbol
    );

    if (abstractToken) {
      return {
        price: abstractToken.currentPrice,
        change: abstractToken.priceChange24h,
        tagSlug: abstractToken.tagSlug || null,
        name: abstractToken.name
      };
    }

    // Check major coins. tagSlug is deliberately null: BTC and ETH used to link to
    // /tags/bitcoin and /tags/ethereum, but those chain-identity tags were deleted
    // (Lam 2026-09-03 — a chain is a facet, never an entity), so the link would 404.
    // The price + delta still render; only the href is gone.
    if (upperSymbol === 'BTC' && cryptoData.bitcoin) {
      return {
        price: cryptoData.bitcoin.currentPrice,
        change: cryptoData.bitcoin.priceChange24h,
        tagSlug: null,
        name: 'Bitcoin'
      };
    }

    if (upperSymbol === 'ETH' && cryptoData.ethereum) {
      return {
        price: cryptoData.ethereum.currentPrice,
        change: cryptoData.ethereum.priceChange24h,
        tagSlug: null,
        name: 'Ethereum'
      };
    }

    return null;
  }, [cryptoData, symbol]);

  // The delta chip, shared by the linked and unlinked renderings.
  const delta = tokenInfo && tokenInfo.change !== undefined && (
    <span
      className={`text-xs font-medium ${
        tokenInfo.change >= 0
          ? 'text-emerald-600 dark:text-emerald-400'
          : 'text-red-500 dark:text-red-400'
      }`}
    >
      {tokenInfo.change >= 0 ? '+' : ''}
      {tokenInfo.change.toFixed(1)}%
    </span>
  );

  // A resolved ENTITY behind the ticker → link to its page.
  if (tokenInfo?.tagSlug) {
    return (
      <Link
        href={entityUrl(tokenInfo.tagSlug, { trailingSlash: false })}
        className="inline-flex items-baseline gap-0.5 text-scout-gold hover:text-scout-gold/90 transition-colors no-underline group"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="font-medium">${symbol}</span>
        {delta}
      </Link>
    );
  }

  // Priced, but no entity page to point at (BTC/ETH — the chain is a facet, not a node).
  // Keep the live delta; drop the href rather than shipping a 404.
  if (tokenInfo) {
    return (
      <span className="inline-flex items-baseline gap-0.5 font-medium text-gray-700 dark:text-gray-300">
        <span>${symbol}</span>
        {delta}
      </span>
    );
  }

  // Unknown ticker - render as plain styled text (still looks like a token)
  return (
    <span className="font-medium text-gray-700 dark:text-gray-300">
      ${symbol}
    </span>
  );
};

export default TokenSymbol;
